/**
 * The inline separated meta strip where something MUST give.
 *
 * Two things separate it from the fact strip. It uses hairline separators
 * rather than a chain of middle dots — a `·` between every pair turns a line
 * of facts into one long string with punctuation in it — and it drops from the
 * tail in an order the CALLER declares, because only the caller knows which of
 * its own facts is the least load-bearing.
 *
 * "Must give" is decided by MEASUREMENT, not by a count. Dropping only past a
 * fixed number of entries meant a two-fact line never dropped anything at all:
 * every entry squeezed instead, and an entry whose value is an object rather
 * than a string cannot squeeze — it is clipped where it stands, which is how a
 * rating count rendered as `32` for `322` and as `8` for `824`. A sliced
 * numeral is not a shorter fact, it is a different and wrong one.
 *
 * So a value that is text may shrink and ends in an ellipsis; a value that is
 * composed — a rating, a chip, a figure — holds its intrinsic width, and the
 * line drops whole entries around it until what remains fits.
 *
 * ONE entry pays the shortfall, and it is the one the caller named. Every text
 * entry used to carry `flex-shrink: 1`, so a shortfall was smeared across all
 * of them in proportion to their own widths and every field on the line came
 * out a fragment: `National Taiwan Univer… │ published · … │ WO, EP, US,…`,
 * where the date entry surrendered enough to lose its ENTIRE payload and keep
 * only its label and separator. The drop order already says which fact is the
 * least load-bearing; the shrink now follows it, so the next entry to be
 * dropped is the only one that yields, the entries above it in the order stay
 * whole, and when the payer reaches the floor below which it carries no
 * payload the line stops squeezing and DROPS it — which is the behaviour the
 * floor was written for and never reached. With nothing left to drop every
 * entry yields again, because an entry clipped flat is worse than one that
 * loses a tail.
 *
 * The measurement is a subscription, not a render loop: two observers, one on
 * the line's available width and one on the width its content actually wants.
 * Dropping an entry narrows the content, the content observer answers, and the
 * next decision is taken from that — so the sequence is driven by the layout
 * engine reporting a real size rather than by a component re-measuring itself
 * after every render its parent happens to make.
 */
"use client";
import {
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactElement,
} from "react";
import {
  CRC_NUMERIC_STYLE,
  CRC_TEXT_MUTED,
  CRC_TRUNCATE_STYLE,
  CRC_TYPE_STYLE,
} from "./cresultcard.tokens";
import {
  CresultcardTextStep,
  type CresultcardFactLineEntry,
} from "./cresultcard.types";

const LINE_GAP = 7;
const TICK_HEIGHT = 9;
const TICK_WIDTH = 1;
/** Four is the point where an inline strip stops being scannable. */
const MAX_FACTS = 4;
/** Sub-pixel rounding is not an overflow. */
const FIT_TOLERANCE = 1;
const NO_DROPS = 0;
const ONE_DROP = 1;
const UNMEASURED_WIDTH = 0;
/** The line always keeps one fact: an empty meta line states nothing at all. */
const MIN_SURVIVORS = 1;
/** Keys, not object identity: callers build their fact arrays inline. */
const SIGNATURE_SEPARATOR = "|";

/** A composed value is an object with its own layout; it is never sliced. */
const ATOMIC_ENTRY_STYLE: CSSProperties = { flex: "0 0 auto" };
/**
 * How narrow a text entry may get before dropping it beats ellipsising it.
 *
 * This floor is what makes the measured drop pass REACHABLE. With `minWidth: 0`
 * a text entry shrinks without limit, so the flex layout always found a fit,
 * `scrollWidth` always equalled the available width, and the reconcile returned
 * on its first check — the measurement was live, correct and permanently
 * satisfied, so no line containing a single text entry ever dropped anything.
 * The static `max` was the only lever left. At the Meta step this is roughly
 * eight characters plus the ellipsis: below it a fact is not shortened, it is
 * unreadable, and the caller's own drop order is the better answer.
 */
const TEXT_ENTRY_FLOOR = 56;
/**
 * The chrome an entry carries IN FRONT of its value: the hairline tick and the
 * gap between that tick and the value. Every entry but the first has it, and it
 * lives inside the entry's own box — so a floor written as the entry's
 * `minWidth` hands the value that much less than it names.
 *
 * It cost four glyphs on a real row. `TypeScript` — the fact a github family
 * declares may never be dropped, because it is what separates six otherwise
 * identical agent repositories — needs 50px and rendered `TypeSc…` in a 48px
 * box: the 56px floor minus a 1px tick minus a 7px gap. Two pixels short of a
 * complete word, and the shortfall was in the arithmetic rather than in the
 * layout. The floor is the VALUE's, so an entry that draws chrome asks for the
 * floor plus its chrome.
 */
const ENTRY_CHROME_WIDTH = TICK_WIDTH + LINE_GAP;

interface TextEntryStyleInput {
  /** The first entry draws no tick and no gap before its value. */
  readonly first: boolean;
  /** Whether this entry is the one paying the line's shortfall. */
  readonly pays: boolean;
}

/**
 * Text can lose its tail gracefully, so it is the class that shrinks — but only
 * the entry the caller's drop order nominates. Everything above it in that
 * order holds its width, which is what turns a shortfall from a smear across
 * every fact into one named fact yielding, and then being dropped.
 */
function textEntryStyle({ first, pays }: TextEntryStyleInput): CSSProperties {
  return {
    flex: pays ? "0 1 auto" : "0 0 auto",
    minWidth: first ? TEXT_ENTRY_FLOOR : TEXT_ENTRY_FLOOR + ENTRY_CHROME_WIDTH,
  };
}

/** The content is measured as one box, so its width is what the line wants. */
const CONTENT_STYLE: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: LINE_GAP,
  minWidth: 0,
  maxWidth: "100%",
};

interface CresultcardFactLineProps {
  readonly facts: readonly CresultcardFactLineEntry[];
  /** Keys, least load-bearing first. Declared by the caller, never guessed. */
  readonly dropOrder: readonly string[];
  readonly max?: number;
}

/** A string or a number sets type; anything else is a composed object. */
function isTextValue(value: CresultcardFactLineEntry["value"]): boolean {
  return typeof value === "string" || typeof value === "number";
}

interface SurvivingFactsInput {
  readonly facts: readonly CresultcardFactLineEntry[];
  readonly droppableKeys: readonly string[];
  readonly drops: number;
}

/**
 * The drop order, narrowed to keys this line actually carries.
 *
 * A caller declares one drop order for a family and hands whichever facts a
 * given row happens to have, so a declared key is routinely absent. Counting
 * drops against the raw order made a "drop one" decision remove nothing at all,
 * and the pass then had to spend another frame to discover that — or ran out of
 * budget with the line still overflowing.
 */
function droppableKeysOf(
  facts: readonly CresultcardFactLineEntry[],
  dropOrder: readonly string[],
): readonly string[] {
  const present = new Set(facts.map((fact) => fact.key));
  return dropOrder.filter((key) => present.has(key));
}

/** Drops from the caller's own order, `drops` deep. */
function survivingFacts({
  facts,
  droppableKeys,
  drops,
}: SurvivingFactsInput): readonly CresultcardFactLineEntry[] {
  if (drops <= NO_DROPS) {
    return facts;
  }
  const doomed = new Set(droppableKeys.slice(0, drops));
  return facts.filter((fact) => !doomed.has(fact.key));
}

export function CresultcardFactLine({
  facts,
  dropOrder,
  max = MAX_FACTS,
}: CresultcardFactLineProps): ReactElement | null {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLSpanElement | null>(null);
  const availableRef = useRef(UNMEASURED_WIDTH);
  // The declared ceiling is the floor of the drop count: past four entries a
  // strip has stopped being scannable whether or not it fits.
  const budget = Math.min(max, MAX_FACTS);
  const overBudget = Math.max(facts.length - budget, NO_DROPS);
  const droppableKeys = droppableKeysOf(facts, dropOrder);
  const maxDrops = Math.min(
    droppableKeys.length,
    Math.max(facts.length - MIN_SURVIVORS, NO_DROPS),
  );
  const signature = [
    facts.map((fact) => fact.key).join(SIGNATURE_SEPARATOR),
    dropOrder.join(SIGNATURE_SEPARATOR),
  ].join(SIGNATURE_SEPARATOR);

  const [drops, setDrops] = useState(overBudget);
  // The caller handed over a different list, so every earlier decision about
  // the old one is stale. Adjusted during render rather than in an effect: the
  // new list must not paint for a frame under the previous list's verdict.
  const [measuredSignature, setMeasuredSignature] = useState(signature);
  if (measuredSignature !== signature) {
    setMeasuredSignature(signature);
    setDrops(overBudget);
  }

  const dropsRef = useRef(drops);
  const maxDropsRef = useRef(maxDrops);
  const overBudgetRef = useRef(overBudget);

  useLayoutEffect((): void => {
    dropsRef.current = drops;
    maxDropsRef.current = maxDrops;
    overBudgetRef.current = overBudget;
  }, [drops, maxDrops, overBudget]);

  useLayoutEffect((): (() => void) | undefined => {
    const root = rootRef.current;
    if (root == null || typeof ResizeObserver === "undefined") {
      return undefined;
    }
    let frame = 0;
    const reconcile = (): void => {
      frame = 0;
      const content = contentRef.current;
      if (content == null) {
        return;
      }
      const available = root.clientWidth;
      // A line that got WIDER may be able to carry facts it gave up, and only
      // the layout above can widen it — dropping never does. That asymmetry is
      // what keeps the pass monotonic instead of oscillating.
      if (available > availableRef.current + FIT_TOLERANCE) {
        availableRef.current = available;
        setDrops(overBudgetRef.current);
        frame = window.requestAnimationFrame(reconcile);
        return;
      }
      availableRef.current = available;
      if (content.scrollWidth <= available + FIT_TOLERANCE) {
        return;
      }
      if (dropsRef.current >= maxDropsRef.current) {
        return;
      }
      // One drop, then look again on the next frame — by which time the commit
      // has landed and the line has a real width to be judged on.
      setDrops(dropsRef.current + ONE_DROP);
      frame = window.requestAnimationFrame(reconcile);
    };
    const schedule = (): void => {
      if (frame === 0) {
        frame = window.requestAnimationFrame(reconcile);
      }
    };
    const observer = new ResizeObserver(schedule);
    observer.observe(root);
    return (): void => {
      observer.disconnect();
      if (frame !== 0) {
        window.cancelAnimationFrame(frame);
      }
    };
    // Keyed on the list, so a line that had no facts to draw — and therefore no
    // node to observe — starts observing the moment it has one.
  }, [signature]);

  const visible = survivingFacts({ droppableKeys, drops, facts });
  if (visible.length === 0) {
    return null;
  }

  // The next fact the caller's own order gives up, and therefore the only one
  // that yields width while it is still on the line. `undefined` means the
  // order is exhausted — nothing here can be dropped, so every text entry goes
  // back to shrinking rather than being cut off flat by the root's clip.
  // Annotated because the index is genuinely out of range once every declared
  // key has been dropped, which the array's own element type does not say.
  const payerKey: string | undefined = droppableKeys[drops];
  const everyTextEntryPays = payerKey === undefined;

  return (
    <div
      data-el="Cresultcard/FactLine/Root"
      data-crc-drops={drops}
      ref={rootRef}
      style={{
        ...CRC_TYPE_STYLE[CresultcardTextStep.Meta],
        ...CRC_NUMERIC_STYLE,
        display: "flex",
        alignItems: "center",
        minWidth: 0,
        overflow: "hidden",
        color: CRC_TEXT_MUTED,
      }}
    >
      <span
        ref={contentRef}
        style={CONTENT_STYLE}
      >
        {visible.map((fact, index) => {
          const text = isTextValue(fact.value);
          const pays = everyTextEntryPays || fact.key === payerKey;
          return (
            <span
              data-el="Cresultcard/FactLine/Entry"
              // Keyed by the entry's own key: every entry answering to one
              // `/Entry` path cannot be told apart by a measurement, which is
              // how an overflow gets attributed to the wrong fact.
              key={fact.key}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: LINE_GAP,
                ...(text
                  ? textEntryStyle({ first: index === 0, pays })
                  : ATOMIC_ENTRY_STYLE),
              }}
            >
              {index > 0 && (
                <span
                  aria-hidden
                  style={{
                    width: TICK_WIDTH,
                    height: TICK_HEIGHT,
                    flex: "0 0 auto",
                    backgroundColor: "rgba(255,255,255,0.12)",
                  }}
                />
              )}
              <span
                style={
                  text
                    ? { ...CRC_TRUNCATE_STYLE, display: "block" }
                    : ATOMIC_ENTRY_STYLE
                }
              >
                {fact.value}
              </span>
            </span>
          );
        })}
      </span>
    </div>
  );
}
